import { useEffect, useState } from "react";

import Modal from "@mui/material/Modal";
import Select from "react-select";

import "../App.css";
import { getSelectStyle } from "../util/util";
import { REGIONS, DEFAULT_BASE_URL, sanitizeBaseUrl } from "../util/baseUrl";

const CUSTOM_REGION_ID = "custom";

const REGION_OPTIONS = [
  ...REGIONS.map((region) => ({ value: region.id, label: region.label })),
  { value: CUSTOM_REGION_ID, label: "Custom (private tenant)" },
];

// Figures out which region dropdown option a baseUrl corresponds to, so the
// modal reopens showing the right selection instead of always defaulting to
// "US" or falling through to an unvalidated custom value.
const regionIdForBaseUrl = (candidateBaseUrl) => {
  const normalized = sanitizeBaseUrl(candidateBaseUrl) || DEFAULT_BASE_URL;
  const match = REGIONS.find((region) => region.baseUrl === normalized);
  return match ? match.id : CUSTOM_REGION_ID;
};

function NavbarModal({
  openModal,
  defaultProjectId,
  defaultFlow,
  setOpenModal,
  postAuthFlow,
  styleId,
  baseUrl,
  theme,
  onUpdateSettings,
  onResetSettings,
}) {
  const [settings, setSettings] = useState({
    settingProject: defaultProjectId || "",
    settingFlow: defaultFlow || "",
    settingPostAuthFlow: postAuthFlow ?? "",
    settingStyle: styleId || "",
    settingRegion: regionIdForBaseUrl(baseUrl),
    settingCustomBaseUrl:
      regionIdForBaseUrl(baseUrl) === CUSTOM_REGION_ID ? baseUrl || "" : "",
  });
  const [baseUrlError, setBaseUrlError] = useState("");

  const handleChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value,
    });
    if (e.target.name === "settingCustomBaseUrl") {
      setBaseUrlError("");
    }
  };

  const handleRegionChange = (option) => {
    setSettings({
      ...settings,
      settingRegion: option.value,
    });
    setBaseUrlError("");
  };

  useEffect(() => {
    setSettings({
      settingProject: defaultProjectId || "",
      settingFlow: defaultFlow || "",
      settingPostAuthFlow: postAuthFlow ?? "",
      settingStyle: styleId || "",
      settingRegion: regionIdForBaseUrl(baseUrl),
      settingCustomBaseUrl:
        regionIdForBaseUrl(baseUrl) === CUSTOM_REGION_ID ? baseUrl || "" : "",
    });
    setBaseUrlError("");
  }, [defaultProjectId, defaultFlow, postAuthFlow, styleId, baseUrl]);

  const handleUpdate = () => {
    let nextBaseUrl = DEFAULT_BASE_URL;

    if (settings.settingRegion === CUSTOM_REGION_ID) {
      const trimmed = settings.settingCustomBaseUrl.trim();
      if (trimmed) {
        const sanitized = sanitizeBaseUrl(trimmed);
        if (!sanitized) {
          setBaseUrlError(
            "Must be an https:// Descope-operated host, e.g. https://api.star5.descope.app for a private tenant.",
          );
          return;
        }
        nextBaseUrl = sanitized;
      }
    } else {
      const region = REGIONS.find((r) => r.id === settings.settingRegion);
      nextBaseUrl = region ? region.baseUrl : DEFAULT_BASE_URL;
    }

    onUpdateSettings({
      projectId: settings.settingProject,
      flowId: settings.settingFlow,
      postAuthFlowId: settings.settingPostAuthFlow,
      styleId: settings.settingStyle,
      baseUrl: nextBaseUrl,
    });
    setOpenModal({ open: false, modalType: "" });
  };

  return (
    <Modal
      open={openModal.open}
      onClose={() => setOpenModal({ open: false, modalType: "" })}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      className="page"
    >
      <div className="question-modal">
        <h1 className="questions-modal-title">{openModal.modalType}</h1>
        {openModal.modalType === "Questions" ? (
          <div className="inner-question-container">
            <p className="question">What is Descope Explorer?</p>
            <p>
              Descope Explorer is an easy way to demo your own project
              authentication flows.
            </p>
            <p className="question">
              How can I add my own project id, flow id, style id, and theme?
            </p>
            <p>
              1. Optionally use the URL on first load, or set everything via the
              Settings form. Your selections are persisted in localStorage.
            </p>
            <p className="question">OR</p>
            <p>
              2. Click on the settings icon on the top right navbar to modify
              all the parameters through input fields.
            </p>
          </div>
        ) : (
          <>
            <p className="settings-tagline">
              Try modifying the default values with your own project info!
            </p>
            <div className="settings-form">
              <label className="settings-label">Project ID:</label>
              <input
                type="text"
                name="settingProject"
                value={settings.settingProject}
                onChange={(e) => handleChange(e)}
              />
              <label className="settings-label">Flow ID:</label>
              <input
                type="text"
                name="settingFlow"
                value={settings.settingFlow}
                onChange={(e) => handleChange(e)}
              />
              <label className="settings-label">
                Post Auth Flow ID (optional):
              </label>
              <p style={{ textAlign: "left", fontSize: "14px" }}>
                This optional parameter allows previewing of flows that require
                authentication to view (eg. Step up, Update User, Invite Users).
              </p>
              <input
                type="text"
                name="settingPostAuthFlow"
                value={settings.settingPostAuthFlow}
                onChange={(e) => handleChange(e)}
              />
              <label className="settings-label">Style ID (optional):</label>
              <p style={{ textAlign: "left", fontSize: "14px" }}>
                This optional parameter allows previewing of different flow
                styles.
              </p>
              <input
                type="text"
                name="settingStyle"
                value={settings.settingStyle}
                onChange={(e) => handleChange(e)}
              />

              <label className="settings-label">Region / Base URL (optional):</label>
              <p style={{ textAlign: "left", fontSize: "14px" }}>
                Choose your project's data residency region, or select Custom
                for a private tenant / custom domain.
              </p>
              <Select
                options={REGION_OPTIONS}
                value={REGION_OPTIONS.find(
                  (option) => option.value === settings.settingRegion,
                )}
                onChange={handleRegionChange}
                styles={getSelectStyle(theme)}
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable={false}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
              {settings.settingRegion === CUSTOM_REGION_ID && (
                <>
                  <input
                    type="text"
                    name="settingCustomBaseUrl"
                    placeholder="https://api.star5.descope.app"
                    value={settings.settingCustomBaseUrl}
                    onChange={(e) => handleChange(e)}
                  />
                  {baseUrlError && (
                    <p style={{ textAlign: "left", fontSize: "13px", color: "#d32f2f" }}>
                      {baseUrlError}
                    </p>
                  )}
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                className="update-btn"
                onClick={() => {
                  onResetSettings();
                  setOpenModal({ open: false, modalType: "" });
                }}
              >
                Reset
              </button>
              <button className="update-btn" onClick={handleUpdate}>
                Update
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default NavbarModal;
